import { Link } from "react-router-dom";

import { EmptyState, Loading } from "../components/StateViews";
import { trpc } from "../trpc";

export function StudyPage() {
  const study = trpc.study.list.useInfiniteQuery(
    { limit: 30 },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const undef = trpc.annotations.myUndefined.useQuery();

  const items = study.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Your study</p>
          <h1 className="mt-3 font-serif text-4xl text-ink">
            Where you’re reading
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/drill?deck=study"
            className="rounded-full border border-line-strong px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk"
          >
            Drill these words
          </Link>
          <Link
            to="/capture"
            className="rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-paper transition-opacity hover:opacity-90"
          >
            Capture a doha
          </Link>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="eyebrow">Dohas you’re following</h2>
        {study.isLoading ? (
          <Loading label="Gathering your study" />
        ) : items.length === 0 ? (
          <div className="mt-4">
            <EmptyState title="Your study is empty.">
              Capture a doha, or open one and add it to your study.
            </EmptyState>
          </div>
        ) : (
          <>
            <ul className="mt-4 divide-y divide-line rounded-2xl border border-line bg-paper-raised">
              {items.map((d) => (
                <li key={d.slug}>
                  <Link
                    to={`/doha/${d.slug}`}
                    className="block px-5 py-4 transition-colors hover:bg-paper-sunk"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-serif text-lg text-ink">
                        Mandal {d.mandal} · Chapter {d.chapter} · Doha {d.doha}
                      </span>
                      <span className="shrink-0 font-mono text-xs text-ink-faint">
                        {d.slug}
                        {d.captureCount > 0 ? ` · ${d.captureCount} 📷` : ""}
                      </span>
                    </div>
                    {d.note ? (
                      <p className="mt-1 line-clamp-2 text-sm text-ink-soft">
                        {d.note}
                      </p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
            {study.hasNextPage ? (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => void study.fetchNextPage()}
                  disabled={study.isFetchingNextPage}
                  className="rounded-full border border-line-strong px-6 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk disabled:opacity-50"
                >
                  {study.isFetchingNextPage ? "Loading…" : "More"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="mt-12">
        <h2 className="eyebrow">Words you’ve flagged</h2>
        {undef.data && undef.data.length > 0 ? (
          <ul className="mt-4 divide-y divide-line rounded-2xl border border-line bg-paper-raised">
            {undef.data.map((a) => (
              <li key={a.id}>
                <Link
                  to={
                    a.dohaSlug
                      ? `/doha/${a.dohaSlug}`
                      : `/capture/${a.captureId}`
                  }
                  className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-paper-sunk"
                >
                  <span className="font-serif text-lg text-ink">{a.label}</span>
                  {a.dohaSlug ? (
                    <span className="font-mono text-xs text-ink-faint">
                      {a.dohaSlug}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-ink-faint">
            Nothing outstanding — every word you’ve marked has a meaning.
          </p>
        )}
      </section>
    </div>
  );
}
